import { toastState } from "@/atoms/system/toast-state";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { ButtonColor } from "@/common/enum/button-color";
import { AdminItemPath } from "@/common/enum/path";
import { imageUrlsToFiles } from "@/common/util/data-url-to-file";
import { PacakgeItemUpdateHeader } from "@/components/header/PackageItemUpdateHeader";
import { DeleteItemModal } from "@/components/modal/DeleteItemModal";
import { ItemDetailHistoryModal } from "@/components/modal/ItemDetailHistoryModal";
import { UpdateItemModal } from "@/components/modal/UpdateItemModal";
import { PackageUpdatePanel } from "@/components/panel/PackageUpdatePanel";
import { PackageItemChildTable } from "@/components/table/package-item-main/PackageItemChildTable";
import { Body14Medium } from "@/components/typography/Body14Medium";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { itemService } from "@/services/item-service";
import { ImageData, urlToImageData } from "@/types/image-data";
import { Button } from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import validator from "validator";
import { PackageChildItemModal } from "../../../components/modal/PackageChildItemModal";
import { ItemDetailLineDto } from "../../../types/dto/item-detail-line-dto";
import { UpdatePackagePayload } from "../../../types/payload/update-package-payload";

const initUpdateItemPayload: UpdatePackagePayload = {
  name: "",
  files: [],
  details: "",
  features: "",
  modelName: "",
  isCataloged: true,
  minOrderQuantity: 0,
  price: 0,
  discountRate: 0,
  isForSale: true,
  isDisplayed: true,
  items: [],
  memos: [],
};

export default function PackageProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [updatePackagePayload, setUpdateItemPayload] =
    useState<UpdatePackagePayload>(initUpdateItemPayload);
  const [isOpenUpdateItemModal, setIsOpenUpdateItemModal] = useState(false);
  const [isOpenDeleteItemModal, setIsOpenDeleteItemModal] = useState(false);
  const [imageDatas, setImageDatas] = useState<ImageData[]>([]);
  const [childItems, setChildItems] = useState<ItemDetailLineDto[]>([]);

  // 원본 데이터를 저장할 state 추가
  const [originalPackageData, setOriginalPackageData] =
    useState<UpdatePackagePayload>(initUpdateItemPayload);
  const [originalImageDatas, setOriginalImageDatas] = useState<ImageData[]>([]);
  const [originalChildItems, setOriginalChildItems] = useState<
    ItemDetailLineDto[]
  >([]);

  const setToast = useSetRecoilState(toastState);
  const isActive = useMemo(() => {
    // 기본 유효성 검사
    const isValidData =
      !validator.isEmpty(updatePackagePayload.name) &&
      !validator.isEmpty(updatePackagePayload.details) &&
      !validator.isEmpty(updatePackagePayload.features) &&
      !validator.isEmpty(updatePackagePayload.modelName) &&
      childItems.length > 1;

    // 데이터 변경 여부 확인
    const isDataChanged =
      updatePackagePayload.name !== originalPackageData.name ||
      updatePackagePayload.details !== originalPackageData.details ||
      updatePackagePayload.features !== originalPackageData.features ||
      updatePackagePayload.modelName !== originalPackageData.modelName ||
      updatePackagePayload.isCataloged !== originalPackageData.isCataloged ||
      updatePackagePayload.minOrderQuantity !==
        originalPackageData.minOrderQuantity ||
      updatePackagePayload.price !== originalPackageData.price ||
      updatePackagePayload.discountRate !== originalPackageData.discountRate ||
      updatePackagePayload.isForSale !== originalPackageData.isForSale ||
      updatePackagePayload.isDisplayed !== originalPackageData.isDisplayed ||
      JSON.stringify(updatePackagePayload.memos) !==
        JSON.stringify(originalPackageData.memos) ||
      imageDatas.length !== originalImageDatas.length ||
      imageDatas.some((img, idx) => img.url !== originalImageDatas[idx]?.url) ||
      childItems.length !== originalChildItems.length ||
      childItems.some(
        (item, idx) =>
          item.childItem.id !== originalChildItems[idx]?.childItem.id ||
          item.quantity !== originalChildItems[idx]?.quantity
      );

    return isValidData && isDataChanged;
  }, [
    updatePackagePayload,
    originalPackageData,
    imageDatas,
    originalImageDatas,
    childItems,
    originalChildItems,
  ]);

  const [isOpenChildItemModal, setIsOpenChildItemModal] = useState(false);

  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);

  const handleAddMemo = () => {
    setUpdateItemPayload((prevPayload) => {
      // 메모가 10개 이상이면 추가하지 않음
      if (prevPayload.memos.length >= 10) return prevPayload;

      return {
        ...prevPayload,
        memos: [...prevPayload.memos, ""],
      };
    });
  };

  const handleMemoChange = (index: number, value: string) => {
    setUpdateItemPayload((prevPayload) => {
      const newMemos = [...prevPayload.memos];
      newMemos[index] = value;
      return {
        ...prevPayload,
        memos: newMemos,
      };
    });
  };

  const handleRemoveMemo = (index: number) => {
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      memos: prevPayload.memos.filter((_, i) => i !== index),
    }));
  };

  const openHistoryModal = () => {
    setIsOpenHistoryModal(true);
  };

  const closeHistoryModal = () => {
    setIsOpenHistoryModal(false);
  };

  const handleOpenChildItemModal = () => {
    setIsOpenChildItemModal(true);
  };

  const handleCloseChildItemModal = () => {
    setIsOpenChildItemModal(false);
  };

  const handleOpenUpdateItemModal = () => {
    setIsOpenUpdateItemModal(true);
  };

  const handleCloseUpdateItemModal = () => {
    setIsOpenUpdateItemModal(false);
  };

  const handleOpenDeleteItemModal = () => {
    setIsOpenDeleteItemModal(true);
  };

  const handleCloseDeleteItemModal = () => {
    setIsOpenDeleteItemModal(false);
  };

  const handleFileChange = (files: File[]) => {
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      files: files,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      [name]: value,
    }));
  };

  const handleCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      isCataloged: value === "YES",
    }));
  };

  const handleUpdateButtonClick = async () => {
    try {
      const updateResponse = await itemService.updatePackage(Number(id), {
        ...updatePackagePayload,
        items: childItems.map((line) => {
          return {
            itemId: line.childItem.id,
            quantity: line.quantity,
          };
        }),
        files: await imageUrlsToFiles(
          imageDatas.map((imageData) => imageData.url)
        ),
      });
      if (!updateResponse.ok) {
        const errorArray = Object.values(
          updateResponse.data.aggregateErrors || {}
        );
        if (errorArray.length > 0) {
          alert(`${errorArray[0]}`);
        } else {
          alert(GLOBAL_ERROR);
        }
        return;
      }
      console.log("Item updated successfully:", updateResponse);
      setToast({
        show: true,
        messages: [`상품을 수정했습니다.`],
      });
      handleCloseUpdateItemModal();
      navigate(AdminItemPath.PACKAGE_PRODUCT);
      // }
    } catch (error) {
      console.error("Error creating item:", error);
    }
  };

  const handleDeleteButtonClick = async () => {
    try {
      await itemService.delete({ id: Number(id) });
      setToast({
        show: true,
        messages: [`상품을 삭제했습니다.`],
      });
      handleCloseDeleteItemModal();
      navigate(AdminItemPath.PACKAGE_PRODUCT);
    } catch (error) {
      console.log(error);
    }
  };

  // getItemDetailById 함수 수정
  const getItemDetailById = async () => {
    try {
      const response = await itemService.getById({ id: Number(id) });
      const newPackageData = {
        name: response.data.name,
        details: response.data.details,
        features: response.data.features,
        modelName: response.data.modelName,
        isCataloged: response.data.isCataloged,
        minOrderQuantity: response.data.minOrderQuantity,
        price: response.data.price,
        discountRate: response.data.discountRate,
        isForSale: response.data.isForSale,
        isDisplayed: response.data.isDisplayed,
        files: [],
        items: [],
        memos: response.data.memos,
      };

      setUpdateItemPayload(newPackageData);
      setOriginalPackageData(newPackageData);

      const newChildItems = response.data.itemLines.map((line) => ({
        quantity: line.quantity,
        childItem: {
          ...line.childItem,
          imageUrls: [],
          details: "",
          features: "",
          discountRate: 0,
          isStar: false,
          numOrdered: 0,
          itemLines: [],
          totalDiscountRate: 0,
          priceBydefaultDiscount: 0,
          totalPrice: 0,
          memos: [],
          link: "",
        },
      }));

      setChildItems(newChildItems);
      setOriginalChildItems(newChildItems);

      const newImageDatas = response.data.imageUrls.map((imageUrl) =>
        urlToImageData(imageUrl)
      );
      setImageDatas(newImageDatas);
      setOriginalImageDatas(newImageDatas);
    } catch (error) {
      console.log(error);
    }
  };

  const addQuantity = (id: number) => {
    setChildItems((prevItems) => {
      return prevItems.map((item) => {
        if (item.childItem.id === id) {
          return {
            ...item,
            quantity: item.quantity + 1,
          };
        }
        return item;
      });
    });
  };

  const subtractQuantity = (id: number) => {
    setChildItems((prevItems) => {
      return prevItems.map((item) => {
        if (item.childItem.id === id) {
          return {
            ...item,
            quantity: Math.max(item.quantity - 1, 1),
          };
        }
        return item;
      });
    });
  };

  const removeItem = (id: number) => {
    setChildItems((prevItems) => {
      return prevItems.filter((item) => item.childItem.id !== id);
    });
  };

  useEffect(() => {
    getItemDetailById();
  }, []);

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <PacakgeItemUpdateHeader
          isActive={isActive}
          handleUpdateButtonClick={handleOpenUpdateItemModal}
          openHistoryModal={openHistoryModal}
        />
        <div className="flex flex-col gap-[24px]">
          <PackageUpdatePanel
            updatePackagePayload={updatePackagePayload}
            imageDatas={imageDatas}
            handleFileChange={handleFileChange}
            handleChange={handleChange}
            handleCatalogChange={handleCatalogChange}
            setImageDatas={setImageDatas}
            handleAddMemo={handleAddMemo}
            handleMemoChange={handleMemoChange}
            handleRemoveMemo={handleRemoveMemo}
          />
          <div className="flex justify-center items-center p-[24px] bg-white rounded-[8px]">
            <div className="flex flex-col justify-center gap-[16px]">
              <Body14Medium>패키지 상품</Body14Medium>
              <PackageItemChildTable
                items={childItems}
                handleOpenChildItemModal={handleOpenChildItemModal}
                addQuantity={addQuantity}
                subtractQuantity={subtractQuantity}
                removeItem={removeItem}
              />
            </div>
          </div>
        </div>
        <Button
          className="w-[117px] mt-[16px]"
          color={ButtonColor.RED}
          onClick={handleOpenDeleteItemModal}
        >
          상품 삭제하기
        </Button>
        {isOpenChildItemModal && (
          <PackageChildItemModal
            // childItems={createItemPayload.items}
            childDetailItems={childItems.map((line) => {
              return line.childItem;
            })}
            setChildItems={(items) => {
              const newlines = items.map((item) => {
                return {
                  quantity: 1,
                  childItem: item,
                };
              });
              setChildItems(newlines);
            }}
            closeModal={handleCloseChildItemModal}
          />
        )}
        {isOpenUpdateItemModal && (
          <UpdateItemModal
            handleSubmit={handleUpdateButtonClick}
            closeModal={handleCloseUpdateItemModal}
          />
        )}

        {isOpenDeleteItemModal && (
          <DeleteItemModal
            handleSubmit={handleDeleteButtonClick}
            closeModal={handleCloseDeleteItemModal}
          />
        )}
        {isOpenHistoryModal && (
          <ItemDetailHistoryModal
            itemId={Number(id)}
            closeModal={closeHistoryModal}
          />
        )}
      </div>
    </PageWrapper>
  );
}
